import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserPlus, Shield, Clock, X, Search, ChevronDown, User } from "lucide-react";
import { PageHeaderActionsRight, PageHeaderTitleSuffix } from "@/contexts/PageHeaderContext";
import {
  ASSIGNABLE_ROLES,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_PERMISSIONS,
  ROLE_DESCRIPTIONS,
  normalizeRole,
} from "@/lib/rbac";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ROLE = "hospital_pa";

const getOrgLabel = (org) => org?.name ?? org?.organization_name ?? org?.label ?? "";
const getOrgId = (org) => String(org?.id ?? org?.organization_id ?? "");

const getOrgTypeForRole = (role) => {
  if (role?.startsWith("hospital_")) return "hospital";
  if (role?.startsWith("company_")) return "company";
  return null;
};

const getUserInitials = (name) =>
  (name ?? "")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const ManagerCombobox = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchManagers = useCallback(async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = { page: 1, limit: 30 };
      if (search?.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/users`, { params });
      if (requestId !== requestIdRef.current) return;
      setResults((res.data?.users ?? []).filter((u) => u?.id));
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error(e);
      setResults([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.id === value) return;
    const match = results.find((u) => u?.id === value);
    if (match) setSelected(match);
  }, [value, results, selected?.id]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchManagers(query);
    }, query ? 300 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [open, query, fetchManagers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (user) => {
    if (!user?.id) return;
    setSelected(user);
    onChange(user.id);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onChange("");
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="user-team-manager"
        aria-label="Select team manager"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 h-10 border border-input bg-background rounded-md text-sm text-left hover:border-[var(--xl-primary)]/50 transition-colors"
      >
        <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        {selected ? (
          <span className="flex-1 min-w-0 truncate font-medium">{selected.name}</span>
        ) : (
          <span className="flex-1 text-muted-foreground">Search manager…</span>
        )}
        {selected ? (
          <X
            className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={handleClear}
            aria-label="Clear manager"
          />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {open ? (
        <div className="absolute z-[100] top-full mt-1 left-0 w-[min(22rem,calc(100vw-2rem))] sm:w-80 bg-white border border-[var(--xl-line)] rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[var(--xl-line)]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                autoFocus
                data-testid="user-team-manager-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or role…"
                className="w-full h-8 pl-8 pr-3 text-sm placeholder:text-muted-foreground bg-[var(--xl-bg)] border border-[var(--xl-line)] rounded-md outline-none focus:border-[var(--xl-primary)]"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto py-1" aria-live="polite">
            {loading && results.length === 0 ? (
              <li className="px-3 py-6 text-xs text-muted-foreground text-center">Searching…</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-6 text-xs text-muted-foreground text-center">No users found</li>
            ) : (
              results.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    data-testid={`user-team-manager-option-${u.id}`}
                    onClick={() => handleSelect(u)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--xl-primary-soft)] transition-colors ${
                      value === u.id ? "bg-[var(--xl-primary-soft)]" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 text-[10px] font-semibold text-muted-foreground">
                      {getUserInitials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      {u.role ? (
                        <Badge
                          className={`mt-0.5 text-[10px] capitalize ${ROLE_COLORS[u.role] ?? ROLE_COLORS.viewer}`}
                        >
                          {ROLE_LABELS[u.role] ?? u.role.replace(/_/g, " ")}
                        </Badge>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = searchParams.get("role")?.trim() || "";
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    team_manager_id: "",
    role: DEFAULT_ROLE,
    organization_id: "",
    zone: "",
    individual_doctor: false,
  });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { params: { page: 1, limit: 200 } });
      setUsers(res.data.users ?? []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!roleFilter) return users;
    const wanted = normalizeRole(roleFilter);
    return users.filter((u) => normalizeRole(u?.role) === wanted);
  }, [users, roleFilter]);

  const handleRoleFilterChange = (value) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (!value || value === "all") params.delete("role");
      else params.set("role", value);
      return params;
    });
  };

  const handleClearRoleFilter = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("role");
      return params;
    });
  };

  const orgType = getOrgTypeForRole(form.role);
  const showOrganization = Boolean(orgType);
  const showIndividualDoctor = form.role === "hospital_doctor";
  const orgFieldLabel = orgType === "company" ? "Company" : "Hospital";
  const orgFieldPlural = orgType === "company" ? "companies" : "hospitals";

  const fetchOrganizations = useCallback(async (type) => {
    if (!type) {
      setOrganizations([]);
      return;
    }
    setOrgsLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, {
        params: { type, page: 1, limit: 100 },
      });
      const items = res.data?.organizations ?? res.data?.items ?? [];
      const typed = items.filter((org) => {
        const itemType = String(org?.type ?? "").toLowerCase();
        if (type === "hospital") return !itemType || itemType === "hospital";
        return itemType === type;
      });
      const backendFiltered = items.length > 0 && typed.length === items.length;
      setOrganizations(backendFiltered ? items : typed);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || `Failed to load ${type === "company" ? "companies" : "hospitals"}`);
      setOrganizations([]);
    }
    setOrgsLoading(false);
  }, []);

  useEffect(() => {
    if (!dialogOpen) return;
    fetchOrganizations(orgType);
  }, [dialogOpen, orgType, fetchOrganizations]);

  const handleCreate = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!form.password || form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
      };
      if (form.team_manager_id) {
        payload.team_manager_id = form.team_manager_id;
      }
      if (getOrgTypeForRole(form.role) && form.organization_id) {
        payload.organization_id = form.organization_id;
      }
      if (form.role === "hospital_doctor") {
        payload.individual_doctor = Boolean(form.individual_doctor);
      }
      const zone = form.zone?.trim();
      if (zone) {
        payload.zone = zone;
      }
      await axios.post(`${API}/users`, payload);
      toast.success("User created. Share the password securely.");
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
  };

  const resetForm = () =>
    setForm({
      name: "",
      email: "",
      password: "",
      team_manager_id: "",
      role: DEFAULT_ROLE,
      organization_id: "",
      zone: "",
      individual_doctor: false,
    });

  const onRoleChange = (newRole) => {
    setForm((prev) => ({
      ...prev,
      role: newRole,
      organization_id: "",
      individual_doctor: newRole === "hospital_doctor" ? prev.individual_doctor : false,
    }));
  };

  return (
    <div className="space-y-4" data-testid="user-management-page">
      <PageHeaderTitleSuffix>
        {!loading ? ` (${filteredUsers.length})` : null}
      </PageHeaderTitleSuffix>

      <PageHeaderActionsRight>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          <Select value={roleFilter || "all"} onValueChange={handleRoleFilterChange}>
            <SelectTrigger
              data-testid="filter-user-role"
              className="h-9 w-[10.5rem] sm:w-48 border-[var(--xl-line2)] bg-white"
              aria-label="Filter by role"
            >
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ASSIGNABLE_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role] ?? role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {roleFilter ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearRoleFilter}
              data-testid="clear-user-role-filter"
              className="h-9 gap-1 text-xs text-[var(--xl-mut)]"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </Button>
          ) : null}
          <Button
            data-testid="add-user-btn"
            onClick={() => setDialogOpen(true)}
            size="sm"
            className="gap-1.5 flex-shrink-0 h-9"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Add User</span>
          </Button>
        </div>
      </PageHeaderActionsRight>

      {/* Users table */}
      <Card className="overflow-hidden">
        <div className="overflow-auto h-[calc(100vh-260px)]">
          <Table className="sticky-table min-w-[1000px]">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u, i) => (
                  <TableRow key={u.id} data-testid={`user-row-${i}`}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar className="w-8 h-8">
                          {u.avatar_url && <AvatarImage src={u.avatar_url} />}
                          <AvatarFallback className="text-[10px] bg-secondary">
                            {u.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge
                        className={`text-[10px] capitalize ${ROLE_COLORS[u.role] ?? ROLE_COLORS.viewer}`}
                      >
                        {ROLE_LABELS[u.role] ?? u.role?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{u.organization || "—"}</TableCell>
                    <TableCell className="text-sm">{u.zone || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {(u.permissions ?? []).slice(0, 3).map((p) => (
                          <Badge key={p} variant="outline" className="text-[10px] capitalize">
                            {p.replace(/_/g, " ")}
                          </Badge>
                        ))}
                        {(u.permissions ?? []).length > 3 && (
                          <Badge variant="outline" className="text-[10px]">
                            +{u.permissions.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={u.status === "active" ? "default" : "secondary"}
                        className="text-[10px]"
                      >
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {u.last_active ? new Date(u.last_active).toLocaleDateString() : "—"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* RBAC info cards — one per role */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {ASSIGNABLE_ROLES.map((role) => (
          <Card key={role} className="card-hover" data-testid={`role-card-${role}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Badge className={`text-xs capitalize ${ROLE_COLORS[role] ?? ROLE_COLORS.viewer}`}>
                  {ROLE_LABELS[role]}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                {ROLE_DESCRIPTIONS[role]}
              </p>
              <div className="flex flex-wrap gap-1">
                {(ROLE_PERMISSIONS[role] ?? []).map((perm) => (
                  <Badge key={perm} variant="outline" className="text-[9px] capitalize">
                    {perm.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create user dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] sm:w-full overflow-visible" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a user account with role-based permissions</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name *</Label>
                <Input
                  data-testid="user-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input
                  data-testid="user-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Initial Password * (min 8 chars)
                </Label>
                <Input
                  data-testid="user-password"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="e.g. XLSync@2026"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Team Manager</Label>
                <ManagerCombobox
                  value={form.team_manager_id}
                  onChange={(id) => setForm((prev) => ({ ...prev, team_manager_id: id }))}
                />
              </div>
            </div>

            <div className={showOrganization ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "space-y-1.5"}>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={onRoleChange}>
                  <SelectTrigger data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    avoidCollisions={false}
                    className="max-h-60"
                  >
                    {ASSIGNABLE_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showOrganization ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">{orgFieldLabel}</Label>
                  <Select
                    value={form.organization_id || undefined}
                    onValueChange={(v) => setForm({ ...form, organization_id: v })}
                    disabled={orgsLoading}
                  >
                    <SelectTrigger data-testid="user-org">
                      <SelectValue placeholder={orgsLoading ? `Loading ${orgFieldPlural}…` : `Select ${orgFieldLabel.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      avoidCollisions={false}
                      className="max-h-60"
                    >
                      {organizations.length === 0 && !orgsLoading ? (
                        <SelectItem value="__none__" disabled>
                          No {orgFieldPlural} found
                        </SelectItem>
                      ) : (
                        organizations.map((org) => {
                          const orgId = getOrgId(org);
                          const label = getOrgLabel(org) || orgId;
                          if (!orgId) return null;
                          return (
                            <SelectItem key={orgId} value={orgId}>
                              {label}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>

            {showIndividualDoctor ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--xl-line)] px-3 py-2.5">
                <div className="min-w-0">
                  <Label htmlFor="individual-doctor" className="text-xs">
                    Individual Doctor
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Mark this doctor as an individual doctor
                  </p>
                </div>
                <Switch
                  id="individual-doctor"
                  checked={Boolean(form.individual_doctor)}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, individual_doctor: Boolean(checked) }))
                  }
                  data-testid="user-individual-doctor"
                  aria-label="Individual doctor"
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label className="text-xs">Zone</Label>
              <Input
                data-testid="user-zone"
                value={form.zone}
                onChange={(e) => setForm({ ...form, zone: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="submit-user">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
